import React, { useState, useEffect } from 'react';
import { Users, Check, X, Trash2, AlertCircle, Shield, Pencil, Plus, ChevronUp, ChevronDown, ChevronsUpDown, Ban, PlayCircle, CornerDownRight, ArrowRight, ArrowLeft, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { userApi } from '../lib/api';
import { supabase } from '../lib/supabase';
import { UserRole } from '../types/auth';
import { useAuth } from '../contexts/AuthContext';
import { ITALIAN_PROVINCES } from '../constants/cities';
import { ITALIAN_REGION_NAMES, getProvincesForRegion } from '../constants/regions';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  city: string;
  region: string | null;
  phone: string;
  role: string;
  created_at: string;
  approval_status: 'pending' | 'approved' | 'rejected';
  is_suspended: boolean;
  birthdate: string | null;
}

interface Permission {
  menu_item: string;
  admin_access: boolean;
  area_leader_access: boolean;
  organizer_access: boolean;
  pr_access: boolean;
  collaboratore_access: boolean;
  display_order: number;
  parent_menu: string | null;
}

interface EditUserModalProps {
  user: UserProfile | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: { name: string; role: UserRole; city?: string; region?: string; phone?: string; email?: string; birthdate?: string | null }) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('collaboratore');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setRole(user.role as UserRole);
      setCity(user.city || '');
      setRegion(user.region || '');
      setPhone(user.phone || '');
      setBirthdate(user.birthdate || '');
    } else {
      setName('');
      setEmail('');
      setRole('collaboratore');
      setCity('');
      setRegion('');
      setPhone('');
      setBirthdate('');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!name.trim()) {
        throw new Error('Il nome è obbligatorio');
      }

      const userData = {
        name: name.trim(),
        role,
        city: city || undefined,
        region: region || undefined,
        phone: phone || undefined,
        email: email.trim() || undefined,
        birthdate: birthdate || null,
      };

      await onSave(userData);
      onClose();
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.message || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">Modifica Staff</h2>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                disabled={loading}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data di nascita</label>
                <input
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                disabled={loading}
              >
                <option value="admin">Amministratore</option>
                <option value="area_leader">Area Leader</option>
                <option value="organizer">Organizzatore</option>
                <option value="pr">PR</option>
                <option value="collaboratore">Collaboratore</option>
              </select>
            </div>

            {role === 'area_leader' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Regione</label>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  disabled={loading}
                >
                  <option value="">Seleziona regione</option>
                  {ITALIAN_REGION_NAMES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            )}

            {(role === 'organizer' || role === 'pr') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Citta</label>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  disabled={loading}
                >
                  <option value="">Nessuna citta</option>
                  {ITALIAN_PROVINCES.map((cityOption) => (
                    <option key={cityOption} value={cityOption}>
                      {cityOption}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                disabled={loading}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={loading}
              >
                {loading ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

interface CreateStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateStaffModal: React.FC<CreateStaffModalProps> = ({ isOpen, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('collaboratore');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setName('');
    setEmail('');
    setRole('collaboratore');
    setCity('');
    setRegion('');
    setPhone('');
    setBirthdate('');
    setError('');
    setGeneratedPassword(null);
    setCopied(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!name.trim()) throw new Error('Il nome è obbligatorio');
      if (!email.trim()) throw new Error('L\'email è obbligatoria');

      const result = await userApi.createStaffUser({
        email: email.trim(),
        name: name.trim().toUpperCase(),
        role,
        city: city || undefined,
        region: region || undefined,
        phone: phone || undefined,
        birthdate: birthdate || undefined,
      });

      setGeneratedPassword(result.password);
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (generatedPassword) {
      navigator.clipboard.writeText(generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">Aggiungi Staff</h2>
            <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {generatedPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium text-sm mb-2">Utente creato con successo!</p>
                <p className="text-green-700 text-sm">Comunica queste credenziali allo staff:</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email</label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm font-mono text-gray-800">{email}</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Password temporanea</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-gray-50 rounded-lg text-sm font-mono text-gray-800 select-all">
                      {generatedPassword}
                    </div>
                    <button
                      onClick={handleCopy}
                      className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                        copied
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {copied ? 'Copiata!' : 'Copia'}
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                L'utente potra' modificare la password dal proprio profilo dopo il primo accesso.
              </p>

              <button
                onClick={handleClose}
                className="w-full mt-4 px-4 py-2.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-medium transition-colors"
              >
                Chiudi
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.toUpperCase())}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 uppercase"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  disabled={loading}
                >
                  <option value="admin">Amministratore</option>
                  <option value="area_leader">Area Leader</option>
                  <option value="organizer">Organizzatore</option>
                  <option value="pr">PR</option>
                  <option value="collaboratore">Collaboratore</option>
                </select>
              </div>

              {role === 'area_leader' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regione</label>
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    disabled={loading}
                  >
                    <option value="">Seleziona regione</option>
                    {ITALIAN_REGION_NAMES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              )}

              {role !== 'area_leader' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Citta</label>
                  <select
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    disabled={loading}
                  >
                    <option value="">Nessuna citta</option>
                    {ITALIAN_PROVINCES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data di nascita</label>
                  <input
                    type="date"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    disabled={loading}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500">
                La password verra' generata automaticamente e mostrata dopo la creazione.
              </p>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                  disabled={loading}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={loading}
                >
                  {loading ? 'Creazione...' : 'Crea Utente'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

type SortField = 'name' | 'city' | 'role' | 'created_at';
type SortDirection = 'asc' | 'desc';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isAreaLeader = currentUser?.role === 'area_leader';
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'area_leader' | 'organizer' | 'collaboratore' | 'pr' | 'all' | 'permissions'>(isAdmin ? 'pending' : isAreaLeader ? 'organizer' : 'pr');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<UserProfile | null>(null);
  const [isCreateStaffOpen, setIsCreateStaffOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const calculateAge = (birthdate: string | null) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'permissions' && permissions.length === 0) {
      loadPermissions();
    }
  }, [activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [pendingData, allData] = await Promise.all([
        userApi.getPendingUsers(),
        userApi.getAllUsers()
      ]);

      setPendingUsers(pendingData);
      setUsers(allData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const permissionsResponse = await supabase
        .from('menu_permissions')
        .select('*')
        .order('display_order');

      if (permissionsResponse.error) throw permissionsResponse.error;
      setPermissions(permissionsResponse.data || []);
    } catch (err: any) {
      console.error('Error loading permissions:', err);
      setError(err.message || 'Errore nel caricamento dei permessi');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    await loadInitialData();
  };

  const handleApprove = async (userId: string) => {
    try {
      setActionLoading(userId);
      await userApi.approveUser(userId);
      await loadUsers();
    } catch (err: any) {
      console.error('Error approving user:', err);
      setError(err.message || 'Errore nell\'approvazione dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: string) => {
    if (!confirm('Sei sicuro di voler rifiutare questo utente?')) return;

    try {
      setActionLoading(userId);
      await userApi.rejectUser(userId);
      await loadUsers();
    } catch (err: any) {
      console.error('Error rejecting user:', err);
      setError(err.message || 'Errore nel rifiuto dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleSuspend = async (user: UserProfile) => {
    const action = user.is_suspended ? 'riattivare' : 'sospendere';
    if (!confirm(`Sei sicuro di voler ${action} questo utente?`)) return;

    try {
      setActionLoading(user.id);
      if (user.is_suspended) {
        await userApi.reactivateUser(user.id);
      } else {
        await userApi.suspendUser(user.id);
      }
      await loadUsers();
    } catch (err: any) {
      console.error('Error toggling suspension:', err);
      setError(err.message || `Errore durante la sospensione dell'utente`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm('Sei sicuro di voler eliminare definitivamente questo utente? Questa azione non può essere annullata.')) return;

    try {
      setActionLoading(userId);
      await userApi.deleteUser(userId);
      await loadUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Errore nell\'eliminazione dell\'utente');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveUser = async (userData: { name: string; role: UserRole; city?: string; region?: string; phone?: string; email?: string; birthdate?: string | null }) => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      setError(null);

      const updateData: Record<string, any> = {
        name: userData.name,
        role: userData.role,
        city: userData.city || null,
        region: userData.region || null,
        phone: userData.phone || null,
        email: userData.email || null,
        birthdate: userData.birthdate || null,
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', selectedUser.id);

      if (updateError) throw updateError;

      await loadUsers();
      setIsEditModalOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Error saving user:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (menuItem: string, role: 'admin' | 'area_leader' | 'organizer' | 'pr' | 'collaboratore', value: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const field = `${role}_access`;
      const { error: updateError } = await supabase
        .from('menu_permissions')
        .update({ [field]: value })
        .eq('menu_item', menuItem);

      if (updateError) throw updateError;

      setPermissions(prevPermissions =>
        prevPermissions.map(p =>
          p.menu_item === menuItem
            ? { ...p, [field]: value }
            : p
        )
      );
    } catch (err: any) {
      console.error('Error updating permission:', err);
      setError(err.message || 'Errore durante l\'aggiornamento dei permessi');
      await loadUsers();
    } finally {
      setLoading(false);
    }
  };

  const getHierarchicalPermissions = () => {
    const topLevel = permissions.filter(p => !p.parent_menu);
    const result: Permission[] = [];
    for (const item of topLevel) {
      result.push(item);
      const children = permissions
        .filter(p => p.parent_menu === item.menu_item)
        .sort((a, b) => a.display_order - b.display_order);
      result.push(...children);
    }
    return result;
  };

  const getParentCandidates = (menuItem: string) => {
    return permissions.filter(p =>
      !p.parent_menu && p.menu_item !== menuItem && p.menu_item !== 'Homepage' && p.menu_item !== 'Gestione Staff'
    );
  };

  const handleSetParent = async (menuItem: string, parentMenu: string | null) => {
    try {
      setError(null);
      const { error: updateError } = await supabase
        .from('menu_permissions')
        .update({ parent_menu: parentMenu })
        .eq('menu_item', menuItem);
      if (updateError) throw updateError;

      setPermissions(prev =>
        prev.map(p => p.menu_item === menuItem ? { ...p, parent_menu: parentMenu } : p)
      );
    } catch (err: any) {
      console.error('Error updating parent menu:', err);
      setError(err.message || 'Errore durante l\'aggiornamento');
    }
  };

  const handleMovePermission = async (index: number, direction: 'up' | 'down') => {
    const item = permissions[index];
    const sameLevel = permissions
      .filter(p => p.parent_menu === item.parent_menu)
      .sort((a, b) => a.display_order - b.display_order);
    const currentIdx = sameLevel.findIndex(p => p.menu_item === item.menu_item);
    const swapIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
    if (swapIdx < 0 || swapIdx >= sameLevel.length) return;

    const reordered = [...sameLevel];
    const [moved] = reordered.splice(currentIdx, 1);
    reordered.splice(swapIdx, 0, moved);

    const baseOrder = Math.min(...sameLevel.map(p => p.display_order));

    try {
      setError(null);

      for (let i = 0; i < reordered.length; i++) {
        const newOrder = baseOrder + i;
        if (reordered[i].display_order !== newOrder) {
          const { error } = await supabase
            .from('menu_permissions')
            .update({ display_order: newOrder })
            .eq('menu_item', reordered[i].menu_item);
          if (error) throw error;
        }
      }

      setPermissions(prev => {
        const updated = prev.map(p => {
          const reorderedIdx = reordered.findIndex(r => r.menu_item === p.menu_item);
          if (reorderedIdx !== -1) return { ...p, display_order: baseOrder + reorderedIdx };
          return p;
        });
        return updated.sort((a, b) => a.display_order - b.display_order);
      });
    } catch (err: any) {
      console.error('Error reordering permissions:', err);
      setError(err.message || 'Errore durante il riordinamento');
      await loadPermissions();
    }
  };

  const getStatusBadge = (user: UserProfile) => {
    if (user.is_suspended) {
      return (
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-200" title="Sospeso" />
        </div>
      );
    }
    switch (user.approval_status) {
      case 'approved':
        return (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 ring-2 ring-green-200" title="Approvato" />
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 ring-2 ring-yellow-200" title="In attesa" />
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 ring-2 ring-red-200" title="Rifiutato" />
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400 ring-2 ring-gray-200" title={user.approval_status} />
          </div>
        );
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">Admin</span>;
      case 'area_leader':
        return <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded-full text-sm">Area Leader</span>;
      case 'organizer':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Organizer</span>;
      case 'pr':
        return <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">PR</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">Collaboratore</span>;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 text-rose-600" />
    ) : (
      <ChevronDown className="h-4 w-4 text-rose-600" />
    );
  };

  const sortUsers = (usersToSort: UserProfile[]) => {
    return [...usersToSort].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'city') {
        aValue = aValue || '';
        bValue = bValue || '';
      }

      if (sortField === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortField === 'role') {
        const roleOrder = { admin: 0, area_leader: 1, organizer: 2, pr: 3, collaboratore: 4 };
        aValue = roleOrder[aValue as keyof typeof roleOrder] ?? 5;
        bValue = roleOrder[bValue as keyof typeof roleOrder] ?? 5;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  if (loading && !users.length && !pendingUsers.length && !permissions.length) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  const getFilteredUsers = () => {
    if (activeTab === 'pending') return pendingUsers;
    if (activeTab === 'all') return users;
    let filtered = users.filter(u => u.role === activeTab);
    if (isAreaLeader && currentUser?.region) {
      const regionProvinces = getProvincesForRegion(currentUser.region);
      filtered = filtered.filter(u => regionProvinces.includes(u.city));
    } else if (!isAdmin && !isAreaLeader && currentUser?.city) {
      filtered = filtered.filter(u => u.city === currentUser.city);
    }
    return filtered;
  };

  const baseDisplayUsers = getFilteredUsers();
  const displayUsers = sortUsers(baseDisplayUsers);

  const totalUsers = users.length;
  const approvedUsers = users.filter(u => u.approval_status === 'approved').length;
  const rejectedUsers = users.filter(u => u.approval_status === 'rejected').length;
  const organizerUsers = users.filter(u => u.role === 'organizer').length;
  const cityFilteredUsers = !isAdmin && currentUser?.city
    ? users.filter(u => u.city === currentUser.city)
    : users;
  const prUsers = cityFilteredUsers.filter(u => u.role === 'pr').length;
  const collaboratoreUsers = cityFilteredUsers.filter(u => u.role === 'collaboratore').length;

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {isAdmin ? (
              <>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm font-medium">Totale Utenti</p>
                      <p className="text-3xl font-bold text-white mt-1">{totalUsers}</p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm font-medium">In Attesa</p>
                      <p className="text-3xl font-bold text-white mt-1">{pendingUsers.length}</p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <AlertCircle className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm font-medium">Approvati</p>
                      <p className="text-3xl font-bold text-white mt-1">{approvedUsers}</p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <Check className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm font-medium">Rifiutati</p>
                      <p className="text-3xl font-bold text-white mt-1">{rejectedUsers}</p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <X className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm font-medium">P.R.</p>
                      <p className="text-3xl font-bold text-white mt-1">{prUsers}</p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm font-medium">Collaboratori</p>
                      <p className="text-3xl font-bold text-white mt-1">{collaboratoreUsers}</p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                {currentUser?.city && (
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 md:col-span-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm font-medium">Citta</p>
                        <p className="text-xl font-bold text-white mt-1">{currentUser.city}</p>
                      </div>
                      <div className="bg-white/20 p-3 rounded-lg">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="mb-6 flex gap-2 flex-wrap">
        {isAdmin && (
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'pending'
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              In Attesa ({pendingUsers.length})
            </div>
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveTab('area_leader')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'area_leader'
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Area Leader ({users.filter(u => u.role === 'area_leader').length})
            </div>
          </button>
        )}
        {(isAdmin || isAreaLeader) && (
          <button
            onClick={() => setActiveTab('organizer')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'organizer'
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Organizer ({organizerUsers})
            </div>
          </button>
        )}
        <button
          onClick={() => setActiveTab('pr')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'pr'
              ? 'bg-rose-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            P.R. ({prUsers})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('collaboratore')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'collaboratore'
              ? 'bg-rose-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Collaboratori ({collaboratoreUsers})
          </div>
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Tutti gli Utenti ({users.length})
            </div>
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveTab('permissions')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'permissions'
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Permessi Menu
            </div>
          </button>
        )}
        {(isAdmin || isAreaLeader) && (
          <button
            onClick={() => setIsCreateStaffOpen(true)}
            className="ml-auto px-4 py-2 rounded-lg font-medium bg-rose-600 text-white hover:bg-rose-700 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Aggiungi Staff
            </div>
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          {activeTab === 'permissions' ? (
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-center py-4 px-3 text-sm font-semibold text-gray-700 w-20">Ordine</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Voce Menu</th>
                  <th className="text-center py-4 px-3 text-sm font-semibold text-gray-700 w-32">Sottomenu</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Admin</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Area Leader</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Organizzatore</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">PR</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Collaboratore</th>
                </tr>
              </thead>
              <tbody>
                {getHierarchicalPermissions().map((permission) => {
                  const isChild = !!permission.parent_menu;
                  const originalIndex = permissions.findIndex(p => p.menu_item === permission.menu_item);
                  const sameLevel = permissions.filter(p => p.parent_menu === permission.parent_menu);
                  const sameLevelIdx = sameLevel.findIndex(p => p.menu_item === permission.menu_item);
                  const hasChildren = permissions.some(p => p.parent_menu === permission.menu_item);
                  const isFirst = sameLevelIdx === 0;
                  const isLast = sameLevelIdx === sameLevel.length - 1;

                  return (
                    <tr
                      key={permission.menu_item}
                      className={`border-b border-gray-100 hover:bg-gray-50 ${isChild ? 'bg-gray-50/50' : ''}`}
                    >
                      <td className="py-2 px-3">
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            onClick={() => handleMovePermission(originalIndex, 'up')}
                            disabled={isFirst}
                            className="p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                            title="Sposta su"
                          >
                            <ChevronUp className="h-4 w-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleMovePermission(originalIndex, 'down')}
                            disabled={isLast}
                            className="p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                            title="Sposta giu"
                          >
                            <ChevronDown className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          {isChild && <CornerDownRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                          <span className={`font-medium ${isChild ? 'text-gray-600 text-sm' : 'text-gray-900'}`}>
                            {permission.menu_item}
                          </span>
                          {hasChildren && (
                            <span className="text-xs bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full">
                              {permissions.filter(p => p.parent_menu === permission.menu_item).length} sotto-voci
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-3">
                        <div className="flex justify-center">
                          {permission.menu_item !== 'Homepage' && permission.menu_item !== 'Gestione Staff' && !hasChildren ? (
                            <select
                              value={permission.parent_menu || ''}
                              onChange={(e) => handleSetParent(permission.menu_item, e.target.value || null)}
                              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-rose-300 focus:border-rose-300 max-w-[130px]"
                            >
                              <option value="">-- Nessuno --</option>
                              {getParentCandidates(permission.menu_item).map(p => (
                                <option key={p.menu_item} value={p.menu_item}>{p.menu_item}</option>
                              ))}
                            </select>
                          ) : hasChildren ? (
                            <span className="text-xs text-gray-400">Padre</span>
                          ) : (
                            <span className="text-xs text-gray-300">--</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-center">
                          <button
                            onClick={() => handlePermissionChange(permission.menu_item, 'admin', !permission.admin_access)}
                            className={`p-2 rounded-full transition-colors ${
                              permission.admin_access
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            } hover:bg-opacity-75`}
                            disabled={permission.menu_item === 'Gestione Staff'}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-center">
                          <button
                            onClick={() => handlePermissionChange(permission.menu_item, 'area_leader', !permission.area_leader_access)}
                            className={`p-2 rounded-full transition-colors ${
                              permission.area_leader_access
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            } hover:bg-opacity-75`}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-center">
                          <button
                            onClick={() => handlePermissionChange(permission.menu_item, 'organizer', !permission.organizer_access)}
                            className={`p-2 rounded-full transition-colors ${
                              permission.organizer_access
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            } hover:bg-opacity-75`}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-center">
                          <button
                            onClick={() => handlePermissionChange(permission.menu_item, 'pr', !permission.pr_access)}
                            className={`p-2 rounded-full transition-colors ${
                              permission.pr_access
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            } hover:bg-opacity-75`}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-center">
                          <button
                            onClick={() => handlePermissionChange(permission.menu_item, 'collaboratore', !permission.collaboratore_access)}
                            className={`p-2 rounded-full transition-colors ${
                              permission.collaboratore_access
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            } hover:bg-opacity-75`}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Stato</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 hover:text-rose-600 transition-colors"
                    >
                      Nome
                      {getSortIcon('name')}
                    </button>
                  </th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Eta</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                    <button
                      onClick={() => handleSort('city')}
                      className="flex items-center gap-2 hover:text-rose-600 transition-colors"
                    >
                      Citta
                      {getSortIcon('city')}
                    </button>
                  </th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Telefono</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {displayUsers.length > 0 ? (
                  displayUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6">{getStatusBadge(user)}</td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => setDetailUser(user)}
                          className="font-medium hover:text-rose-600 transition-colors text-left"
                        >
                          {user.name}
                        </button>
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        {calculateAge(user.birthdate) !== null ? calculateAge(user.birthdate) : '-'}
                      </td>
                      <td className="py-4 px-6 text-gray-600">{user.city || '-'}</td>
                      <td className="py-4 px-6 text-gray-600">{user.phone || '-'}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          {isAdmin && activeTab === 'pending' && user.approval_status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(user.id)}
                                disabled={actionLoading === user.id}
                                className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                                title="Approva"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleReject(user.id)}
                                disabled={actionLoading === user.id}
                                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                                title="Rifiuta"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {(isAdmin || isAreaLeader) && (activeTab === 'all' || activeTab === 'area_leader' || activeTab === 'organizer' || activeTab === 'pr' || activeTab === 'collaboratore') && user.approval_status === 'approved' && (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setIsEditModalOpen(true);
                              }}
                              disabled={actionLoading === user.id}
                              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                              title="Modifica"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {isAdmin && user.approval_status === 'approved' && user.role !== 'admin' && (
                            <button
                              onClick={() => handleToggleSuspend(user)}
                              disabled={actionLoading === user.id}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                user.is_suspended
                                  ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                  : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                              }`}
                              title={user.is_suspended ? 'Riattiva' : 'Sospendi'}
                            >
                              {user.is_suspended ? <PlayCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                            </button>
                          )}
                          {isAdmin && user.approval_status === 'rejected' && (
                            <button
                              onClick={() => handleApprove(user.id)}
                              disabled={actionLoading === user.id}
                              className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                              title="Approva"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(user.id)}
                              disabled={actionLoading === user.id}
                              className="p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                              title="Elimina"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>
                        {activeTab === 'pending' && 'Nessun utente in attesa di approvazione'}
                        {activeTab === 'area_leader' && 'Nessun area leader registrato'}
                        {activeTab === 'organizer' && 'Nessun organizer registrato'}
                        {activeTab === 'pr' && 'Nessun P.R. registrato'}
                        {activeTab === 'collaboratore' && 'Nessun collaboratore registrato'}
                        {activeTab === 'all' && 'Nessun utente registrato'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <EditUserModal
        user={selectedUser}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
      />

      <CreateStaffModal
        isOpen={isCreateStaffOpen}
        onClose={() => setIsCreateStaffOpen(false)}
        onCreated={loadInitialData}
      />

      {detailUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-white">{detailUser.name}</h2>
                  <p className="text-rose-100 text-sm mt-1">
                    {getRoleBadge(detailUser.role)}
                  </p>
                </div>
                <button
                  onClick={() => setDetailUser(null)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-50 rounded-lg">
                    <Mail className="h-4 w-4 text-rose-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">{detailUser.email || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-50 rounded-lg">
                    <Phone className="h-4 w-4 text-rose-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Telefono</p>
                    <p className="text-sm font-medium text-gray-900">{detailUser.phone || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-50 rounded-lg">
                    <MapPin className="h-4 w-4 text-rose-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Citta</p>
                    <p className="text-sm font-medium text-gray-900">{detailUser.city || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-50 rounded-lg">
                    <Calendar className="h-4 w-4 text-rose-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Data di nascita</p>
                    <p className="text-sm font-medium text-gray-900">
                      {detailUser.birthdate
                        ? `${new Date(detailUser.birthdate).toLocaleDateString('it-IT')} (${calculateAge(detailUser.birthdate)} anni)`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Stato</p>
                  <div className="mt-1">{getStatusBadge(detailUser)}</div>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Data registrazione</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(detailUser.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              {isAdmin && detailUser.approval_status === 'approved' && (
                <button
                  onClick={() => {
                    setSelectedUser(detailUser);
                    setIsEditModalOpen(true);
                    setDetailUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Modifica
                </button>
              )}
              <button
                onClick={() => setDetailUser(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
